(() => {
  if (!customElements.get('db-audio-player')) {
    document.addEventListener('play', (event) => {
      if (!(event.target instanceof HTMLAudioElement)) return;
      document.querySelectorAll('audio').forEach((audio) => {
        if (audio !== event.target) audio.pause();
      });
    }, true);

    class DbAudioPlayer extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.audio = this.querySelector('audio');
        if (!this.audio) return;
        this.controls = this.querySelector('.db-product-audio-controls');
        this.toggle = this.querySelector('button');
        this.seek = this.querySelector('input[type="range"]');
        this.elapsed = this.querySelector('[data-elapsed]');
        this.duration = this.querySelector('[data-duration]');
        this.progress = this.querySelector('[data-progress]');
        this.error = this.querySelector('.db-product-audio-error');
        if (!this.controls || !this.toggle || !this.seek || !this.elapsed || !this.duration || !this.progress || !this.error) return;
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.toggle.addEventListener('click', () => this.togglePlayback(), options);
        this.seek.addEventListener('input', () => {
          if (Number.isFinite(this.audio.duration) && this.audio.duration > 0) {
            this.audio.currentTime = Number(this.seek.value) / 100 * this.audio.duration;
            this.update();
          }
        }, options);
        ['loadedmetadata', 'durationchange', 'timeupdate', 'play', 'pause', 'ended', 'emptied'].forEach((name) => {
          this.audio.addEventListener(name, () => this.update(), options);
        });
        this.audio.addEventListener('error', () => this.useFallback(), options);
        this.update();
        if (this.audio.error) {
          this.useFallback();
        } else {
          this.audio.controls = false;
          this.controls.hidden = false;
        }
      }

      disconnectedCallback() {
        this.events?.abort();
        this.events = null;
        this.audio?.pause();
        if (this.audio) this.audio.controls = true;
        if (this.controls) this.controls.hidden = true;
      }

      async togglePlayback() {
        if (!this.audio.paused) {
          this.audio.pause();
          return;
        }
        try {
          await this.audio.play();
        } catch (error) {
          if (this.isConnected && error.name !== 'AbortError') this.useFallback();
        }
      }

      useFallback() {
        this.audio.controls = true;
        this.controls.hidden = true;
        this.error.textContent = this.dataset.error;
        this.error.hidden = false;
      }

      update() {
        const total = this.audio.duration;
        const valid = Number.isFinite(total) && total > 0;
        const current = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
        const percent = valid ? Math.min(100, Math.max(0, current / total * 100)) : 0;
        this.seek.disabled = !valid;
        this.seek.value = String(percent);
        this.seek.setAttribute('aria-valuetext', `${this.formatTime(current)} / ${valid ? this.formatTime(total) : '—'}`);
        this.progress.style.width = `${percent}%`;
        this.elapsed.textContent = this.formatTime(current);
        this.duration.textContent = valid ? this.formatTime(total) : '—';
        const playing = !this.audio.paused && !this.audio.ended;
        this.toggle.setAttribute('aria-label', playing ? this.dataset.pause : this.dataset.play);
        this.querySelector('[data-play-icon]').hidden = playing;
        this.querySelector('[data-pause-icon]').hidden = !playing;
      }

      formatTime(seconds) {
        const value = Math.max(0, Math.floor(seconds));
        return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
      }
    }
    customElements.define('db-audio-player', DbAudioPlayer);
  }

  if (!customElements.get('db-product-video')) {
    class DbProductVideo extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        const poster = this.querySelector('button');
        if (!poster) return;
        this.events = new AbortController();
        poster.addEventListener('click', () => {
          const mount = this.querySelector('.db-product-video__mount');
          const template = this.querySelector('template');
          if (!mount || !template || mount.childElementCount) return;
          const player = template.content.firstElementChild.cloneNode(true);
          mount.appendChild(player);
          mount.hidden = false;
          poster.hidden = true;
          player.tabIndex = 0;
          player.focus();
        }, { signal: this.events.signal });
      }

      disconnectedCallback() {
        this.events?.abort();
        this.events = null;
        this.querySelector('video')?.pause();
      }
    }
    customElements.define('db-product-video', DbProductVideo);
  }
})();
